import { Button, Select, InputNumber, Table, Modal } from 'antd';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { analytics } from 'src/services';
import { useDispatch, useSelector } from 'react-redux';
import {
  createAppointmentReport,
  getReportsList,
  downloadAppointmentReport,
} from 'src/store/actions/settings.action';
import { downloadableReportUrlSlice } from 'src/store/slices/settings.slice';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { SyncOutlined } from '@ant-design/icons';

const Reports = () => {
  const { user } = useAuth();
  const [timeValue, setTimeValue] = useState(10);
  const [timeUnit, setTimeUnit] = useState('days');
  const [pagination, setPagination] = useState({
    per_page: 5,
    current: 1,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [s3Key, setS3Key] = useState('');
  const [reportType, setReportType] = useState(null);
  const [downloadVisible, setDownloadVisible] = useState(false);

  const { isEntitled: isFollowup } = useFeatureEntitlements('followup');
  const { isEntitled: isIntake } = useFeatureEntitlements('intake');
  const { isEntitled: hasReferralsAccess } = useFeatureEntitlements('referral');

  const dispatch = useDispatch();

  const { reportsList, reportsListLoading, downloadableReportUrl } =
    useSelector((state) => state?.appointmentSettingsState);

  const handleTimeValueChange = (value) => {
    setTimeValue(value);
  };

  const handleTimeUnitChange = (value) => {
    setTimeUnit(value);
  };

  useEffect(() => {
    setTableLoading(reportsListLoading);
  }, [reportsListLoading]);

  useEffect(() => {
    setTableData(reportsList);
  }, [reportsList]);

  useEffect(() => {
    dispatch(getReportsList());
  }, [dispatch]);

  useEffect(() => {
    if (downloadableReportUrl && s3Key) {
      const link = document.createElement('a');
      link.href = downloadableReportUrl;
      link.download = s3Key.split('/').pop();
      document.body.appendChild(link);
      link.click();
      link.remove();
      dispatch(downloadableReportUrlSlice(null));
      setS3Key('');
    }
  }, [downloadableReportUrl, dispatch, s3Key]);

  const handlePageChange = (page) => {
    setPagination({
      ...pagination,
      current: page,
    });
  };

  const convertToDays = (value, unit) => {
    switch (unit) {
      case 'days':
        return value;
      case 'weeks':
        return value * 7;
      case 'months':
        return value * 30; // Approximate conversion
      default:
        return value;
    }
  };

  const columns = [
    {
      title: 'Report Name',
      dataIndex: 'key',
      key: 'key',
      render: (text) => {
        return (
          <Link
            to="#"
            onClick={() => {
              setS3Key(text);
              setDownloadVisible(true);
            }}
            className="hyperlink-style"
          >
            {text.split('/').pop()}
          </Link>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'last_modified',
      key: 'last_modified',
      render: (text) => {
        const browserTimezone = moment.tz.guess();
        return (
          <span>
            {moment.tz(text, browserTimezone).format('MM/DD/YYYY hh:mm A')}
          </span>
        );
      },
    },
  ];

  const handleSubmit = () => {
    const reportTypeLabels = {
      appointments: 'Appointments Report',
      inbound_leads: 'Inbound Leads Report',
      inbox: 'Inbox Report',
    };

    analytics.track(
      `Clicked Generate ${reportTypeLabels[reportType] || 'Report'}`,
      {
        email: user?.email,
        report_type: reportType,
        time_period: `${timeValue} ${timeUnit}`,
      }
    );

    const daysBack = convertToDays(timeValue, timeUnit);

    const payload = {
      days_back: daysBack,
      type: reportType,
    };

    dispatch(createAppointmentReport(payload));
  };

  const handleReportTypeChange = (value) => {
    setReportType(value);
  };

  const handleDownloadClose = () => {
    setDownloadVisible(false);
    dispatch(downloadableReportUrlSlice(null));
    setS3Key('');
  };

  const downloadFileHandler = () => {
    dispatch(downloadAppointmentReport(s3Key));
    setDownloadVisible(false);
  };

  return (
    <>
      <div className="flex-1 pt-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex flex-col gap-3">
            <div className="text-lg font-bold font-sans pl-1 mb-2">
              Generate Report
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-sm font-sans pl-1 mb-2">
                Select the time period for the report (e.g., last 10 days, last
                2 weeks)
              </div>
              <div className="flex gap-2 w-1/5">
                <InputNumber
                  value={timeValue}
                  onChange={handleTimeValueChange}
                  min={1}
                  max={365}
                  style={{
                    borderColor: '#d9d9d9',
                    borderRadius: '6px',
                    flex: 1,
                    textAlign: 'center',
                    paddingTop: '10px',
                    paddingLeft: '10px',
                    width: '100px',
                  }}
                  size="small"
                  placeholder="Enter number"
                />
                <Select
                  value={timeUnit}
                  onChange={handleTimeUnitChange}
                  style={{
                    borderColor: '#d9d9d9',
                    borderRadius: '6px',
                    width: '150px',
                  }}
                  size="small"
                  className={classNames(
                    'font-sans border border-gray-200',
                    'hover:border-blue-400 focus:border-blue-400 rounded-md',
                    'single-select'
                  )}
                >
                  <Select.Option value="days">Days</Select.Option>
                  <Select.Option value="weeks">Weeks</Select.Option>
                  <Select.Option value="months">Months</Select.Option>
                </Select>
              </div>
              <div className="text-sm font-sans pl-1 mb-2 mt-2">
                Report type
              </div>
              <div className="flex gap-2 w-1/3 items-center">
                <Select
                  value={reportType}
                  onChange={handleReportTypeChange}
                  style={{
                    borderColor: '#d9d9d9',
                    borderRadius: '6px',
                    width: '250px',
                  }}
                  size="small"
                  placeholder="Select report type"
                  className={classNames(
                    'font-sans border border-gray-200',
                    'hover:border-blue-400 focus:border-blue-400 rounded-md',
                    'single-select'
                  )}
                >
                  {(isFollowup || isIntake) && (
                    <Select.Option value="appointments">
                      Appointments
                    </Select.Option>
                  )}
                  {hasReferralsAccess && (
                    <Select.Option value="inbox">Inbox</Select.Option>
                  )}
                  {hasReferralsAccess && (
                    <Select.Option value="inbound_leads">
                      Inbound Leads
                    </Select.Option>
                  )}
                </Select>
              </div>
              <div className="flex justify-center w-full">
                <Button
                  onClick={handleSubmit}
                  disabled={!reportType}
                  className={classNames(
                    'mt-5  w-2/4 md:w-1/3 lg:w-1/3',
                    reportType ? 'ih-button-primary' : 'ih-button-ghost'
                  )}
                >
                  Generate report
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            className="ih-button-primary"
            onClick={() => {
              dispatch(getReportsList());
              setS3Key('');
              dispatch(downloadableReportUrlSlice(null));
              setPagination({
                ...pagination,
                current: 1,
              });
            }}
            size="large"
            key="refresh-reports"
          >
            <div className="flex gap-1 pt-0.5">
              {reportsListLoading ? <SyncOutlined spin /> : <SyncOutlined />}
              <span className="font-sans text-sm ml-1">Refresh reports</span>
            </div>
          </Button>
        </div>
        <div className="mt-4">
          <Table
            className="encounter-history-table"
            loading={tableLoading}
            dataSource={tableData}
            columns={columns}
            rowKey={(record) => record.key}
            pagination={{
              total: tableData?.length,
              pageSize: pagination.per_page,
              current: pagination.current,
              onChange: handlePageChange,
            }}
          />
        </div>
      </div>
      <Modal
        open={downloadVisible}
        closable={false}
        centered={true}
        styles={{ body: { height: '35px' } }}
        footer={[
          <div className="flex justify-end gap-1" key="action-buttons">
            <button
              key="back"
              className="bg-[#A9A9A9] hover:bg-[#899499] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={handleDownloadClose}
            >
              Cancel
            </button>
            <button
              key="submit"
              className="bg-[#00D090] hover:bg-[#059669] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={downloadFileHandler}
            >
              Download
            </button>
          </div>,
        ]}
      >
        <div>
          <p>Are you sure you want to download this file?</p>
        </div>
      </Modal>
    </>
  );
};

export default Reports;
